import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Modal for generating + revoking caregiver/admin invite links.
 * Owns its own form state + the live list of pending invites. Calls
 * `onChanged()` after each mutation so the parent can refresh counts.
 */
export default function InviteCaregiverModal({ invites, onClose, onChanged }) {
  const [form, setForm] = useState({ email: "", name: "", role: "caregiver", ttl_days: 10 });
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(invites || []);

  useEffect(() => {
    setPending(invites || []);
  }, [invites]);

  const create = async () => {
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/invites", form);
      setPending([data, ...pending]);
      toast.success("Invite created — copy and share the link");
      setForm({ email: "", name: "", role: "caregiver", ttl_days: 10 });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? The link will stop working immediately.")) return;
    try {
      await api.delete(`/admin/invites/${id}`);
      setPending(pending.filter((i) => i.id !== id));
      toast.success("Invite revoked");
      onChanged();
    } catch {
      toast.error("Could not revoke");
    }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="invite-caregiver-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h3 className="font-heading text-lg font-medium">Invite a caregiver</h3>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost p-1.5"
            data-testid="invite-modal-close"
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-clinical-subtext">
            We'll generate a secure link. Send it to the caregiver — they'll set their own
            password and acknowledge the HIPAA notice when they accept.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-clinical"
                data-testid="invite-email-input"
                placeholder="caregiver@example.com"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Name (optional)</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-clinical"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="input-clinical"
                data-testid="invite-role-select"
              >
                <option value="caregiver">Caregiver</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Link expires in (days)</label>
              <input
                type="number"
                min={1}
                max={60}
                value={form.ttl_days}
                onChange={(e) => setForm({ ...form, ttl_days: Number(e.target.value || 10) })}
                className="input-clinical"
                data-testid="invite-ttl-input"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={create}
            disabled={saving}
            className="btn-primary"
            data-testid="invite-create-button"
          >
            {saving ? "Generating…" : "Generate invite link"}
          </button>

          {pending.length > 0 && (
            <div className="border-t border-clinical-border pt-4">
              <div className="label-eyebrow mb-2">Pending invites ({pending.length})</div>
              <ul className="space-y-2" data-testid="pending-invites-list">
                {pending.map((inv) => {
                  const link = `${window.location.origin}/invite/${inv.token}`;
                  const isExpired = inv.expired;
                  return (
                    <li
                      key={inv.id}
                      className={`rounded-lg border p-3 ${isExpired ? "border-amber-200 bg-amber-50" : "border-clinical-border"}`}
                      data-testid={`invite-row-${inv.id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className="font-medium text-sm">{inv.invited_email}</span>
                        {inv.invited_name && (
                          <span className="text-xs text-clinical-subtext">· {inv.invited_name}</span>
                        )}
                        <span className="pill bg-clinical-bg text-clinical-subtext capitalize">{inv.role}</span>
                        {isExpired && <span className="pill bg-amber-100 text-amber-800">Expired</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={link}
                          className="input-clinical text-xs font-mono"
                          onFocus={(e) => e.target.select()}
                          data-testid={`invite-link-${inv.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => copyLink(inv.token)}
                          className="btn-secondary !px-3 !py-1.5 text-xs"
                          data-testid={`invite-copy-${inv.id}`}
                        >
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => revoke(inv.id)}
                          className="btn-ghost !text-destructive !px-2 !py-1.5 text-xs"
                          data-testid={`invite-revoke-${inv.id}`}
                        >
                          Revoke
                        </button>
                      </div>
                      <div className="text-[10px] text-clinical-subtext mt-1">
                        Expires {inv.expires_at?.slice(0, 10) || "—"}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
